import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  Button,
  Input,
  Field,
  Card,
  Title1,
  Body1,
  tokens,
} from '@fluentui/react-components';
import { AmplifyLogo } from '@/components/common/AmplifyLogo';

// Hardcoded credentials for simple authentication
const VALID_USERNAME = 'cortex-admin';
const VALID_PASSWORD = 'CortexFlow2024!';

interface SimpleAuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => boolean;
  logout: () => void;
}

const SimpleAuthContext = createContext<SimpleAuthContextType | null>(null);

export const useSimpleAuth = () => {
  const context = useContext(SimpleAuthContext);
  if (!context) {
    throw new Error('useSimpleAuth must be used within SimpleAuthProvider');
  }
  return context;
};

interface SimpleAuthProviderProps {
  children: React.ReactNode;
}

export const SimpleAuthProvider: React.FC<SimpleAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated from session storage
    const authStatus = sessionStorage.getItem('cortex-auth-status');
    if (authStatus === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  const login = (username: string, password: string): boolean => {
    if (username === VALID_USERNAME && password === VALID_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('cortex-auth-status', 'authenticated');
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('cortex-auth-status');
  };

  const value = {
    isAuthenticated,
    login,
    logout,
  };

  return (
    <SimpleAuthContext.Provider value={value}>
      {isAuthenticated ? children : <LoginScreen />}
    </SimpleAuthContext.Provider>
  );
};

const LoginScreen: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useSimpleAuth();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Small delay to show loading state
    setTimeout(() => {
      const success = login(username, password);
      if (!success) {
        setError('Invalid username or password');
        setPassword(''); // Clear password on error
      }
      setIsLoading(false);
    }, 500);
  };

  const cardStyles = {
    width: '400px',
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalL,
  };

  const containerStyles = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingHorizontalM,
  };

  const logoStyles = {
    marginBottom: tokens.spacingVerticalL,
  };

  return (
    <div style={containerStyles}>
      <div style={logoStyles}>
        <AmplifyLogo />
      </div>

      <Card style={cardStyles}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
          <div style={{ textAlign: 'center' }}>
            <Title1>Sign In</Title1>
            <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
              Enter your credentials to access CortexFlow
            </Body1>
          </div>

          {error && (
            <div style={{
              padding: tokens.spacingVerticalS,
              backgroundColor: tokens.colorPaletteRedBackground2,
              border: `1px solid ${tokens.colorPaletteRedBorder2}`,
              borderRadius: tokens.borderRadiusSmall,
              color: tokens.colorPaletteRedForeground2
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
            <Field label="Username" required>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                autoComplete="username"
                disabled={isLoading}
              />
            </Field>

            <Field label="Password" required>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                autoComplete="current-password"
                disabled={isLoading}
              />
            </Field>

            <Button
              type="submit"
              appearance="primary"
              disabled={!username || !password || isLoading}
              style={{ marginTop: tokens.spacingVerticalS }}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div style={{
            textAlign: 'center',
            fontSize: tokens.fontSizeBase200,
            color: tokens.colorNeutralForeground3,
            marginTop: tokens.spacingVerticalS,
            borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
            paddingTop: tokens.spacingVerticalS
          }}>
            Demo credentials provided separately
          </div>
        </div>
      </Card>
    </div>
  );
};
